import { Button, Card, Flex, Heading } from '@utilitywarehouse/hearth-react';

import type { AppointmentParticipant } from '@generated/gql/graphql';
import { AppointmentParticipantType } from '@generated/gql/graphql';

import { LobbyListItem } from './ListItem';
import { LobbyListItemPlaceholder } from './ListItemPlaceholder';

interface LobbyListProps {
  participants: AppointmentParticipant[];
  userId: string;
  canStart: boolean;
  isRemoteSupport: boolean;
  isSupported: boolean;
  onStart: () => void;
}

const LobbyList = ({
  participants,
  userId,
  isRemoteSupport,
  isSupported,
  canStart,
  onStart,
}: LobbyListProps) => {
  const you = participants.find((participant) => participant.id === userId);

  // participants excluding current user
  const filteredParticipants = participants.filter(
    (participant) => participant.id !== userId
  );
  const hasCustomer = participants.some(
    (participant) => participant.type === AppointmentParticipantType.Customer
  );
  const hasSupport = participants.some(
    (participant) =>
      participant.type === AppointmentParticipantType.SupportingPartner
  );

  if (!you) return null;

  return (
    <Card
      variant="subtle"
      colorScheme="neutralStrong"
      paddingNone
      direction="column"
      className="remote__LobbyList"
      spacing="xl"
    >
      <Heading size="sm" className="remote__LobbyListTitle">
        In the lobby ({participants.length})
      </Heading>
      <Flex direction="column" className="remote__LobbyListItems">
        <LobbyListItem
          name={you.name}
          userType={you.type}
          participantId={you.id}
          userId={userId}
        />
        {filteredParticipants.map((participant) => (
          <LobbyListItem
            name={participant.name}
            userType={participant.type}
            participantId={participant.id}
            userId={userId}
          />
        ))}
        {!hasCustomer && !isRemoteSupport && (
          <LobbyListItemPlaceholder
            userType={AppointmentParticipantType.Customer}
            description="You can start presenting as soon as they arrive."
          />
        )}
        {isSupported && !hasSupport && (
          <LobbyListItemPlaceholder
            userType={AppointmentParticipantType.SupportingPartner}
            description="They will join the appointment in a moment."
          />
        )}
      </Flex>
      {you.type === AppointmentParticipantType.Partner && (
        <Flex
          className="remote__LobbyActions"
          direction="column"
          alignItems={{ mobile: 'stretch', tablet: 'start' }}
        >
          <Button
            variant="solid"
            colorScheme="highlight"
            size="md"
            disabled={!canStart}
            onClick={onStart}
            data-testid="appointment-start"
          >
            Start presentation
          </Button>
        </Flex>
      )}
    </Card>
  );
};

export { LobbyList };
